// SW-visible logged-in flag store.
//
// Paired with `lib/core/web/sw_logged_in_flag.dart` (Dart writer)
// and `web/firebase-messaging-sw.js` (SW reader). Uses a small
// dedicated IndexedDB (`ski2_sw_state`, store `flags`, key `loggedIn`)
// that both the tab and the service worker can read/write on the
// same origin.
//
// Ordinary auth updates retain a best-effort API. Destructive reset uses
// setLoggedInVerified: a failed write/read must keep reset pending. The SW's
// legacy missing-value fallback is not a confidentiality guarantee.

const DB_NAME = 'ski2_sw_state';
const DB_VERSION = 1;
const STORE = 'flags';
const KEY_LOGGED_IN = 'loggedIn';

function openStateDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    let abandoned = false;
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => {
      if (abandoned) req.result.close();
      else resolve(req.result);
    };
    req.onerror = () => reject(req.error);
    // Fail-fast on `blocked` — matches the round-106 fix on the
    // KEK-wrap IDB (b6b6c8b3). Silent hang here would delay the
    // login auth-flip.
    req.onblocked = () => {
      abandoned = true;
      reject(new Error('ski2_sw_state open blocked'));
    };
  });
}

function writeFlag(db, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('tx aborted'));
    tx.objectStore(STORE).put(value, KEY_LOGGED_IN);
  });
}

function readFlag(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY_LOGGED_IN);
    let out;
    req.onsuccess = () => {
      out = req.result;
    };
    tx.oncomplete = () => resolve(out);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('tx aborted'));
  });
}

// Resolve true only after both transactions commit and the stored boolean
// matches. Separate transactions detect a no-op write or intervening update;
// they do not prevent a later login in another tab.
export async function setLoggedInVerified(value) {
  if (typeof value !== 'boolean') throw new TypeError('Expected login boolean');
  const db = await openStateDB();
  try {
    await writeFlag(db, value);
    if (await readFlag(db) !== value) {
      throw new Error('SW login flag verification failed');
    }
    return true;
  } finally {
    db.close();
  }
}

export async function setLoggedIn(value) {
  try {
    await setLoggedInVerified(!!value);
  } catch (_) {
    console.warn('ski2SwState.setLoggedIn failed');
  }
}

// Best-effort in-tab reader; the service worker has its own reader.
export async function readLoggedIn() {
  try {
    const db = await openStateDB();
    try {
      return await readFlag(db);
    } finally {
      db.close();
    }
  } catch (e) {
    console.warn('ski2SwState.readLoggedIn failed:', e && e.message);
    return undefined;
  }
}
